import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";

export type DsarKind = "export" | "delete" | "rectify";
export type DsarStatus = "pending" | "in_progress" | "completed" | "rejected";

export interface DsarRequest {
  id: string;
  user_id: string;
  kind: DsarKind;
  status: DsarStatus;
  notes: string | null;
  fulfilled_at: string | null;
  fulfillment_url: string | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
}

export const DSAR_KIND_LABELS: Record<DsarKind, string> = {
  export: "Export my data",
  delete: "Delete my account",
  rectify: "Correct my data",
};

export function useDsar() {
  const { user } = useAuth();
  const [requests, setRequests] = useState<DsarRequest[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) {
      setRequests([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data } = await supabase
      .from("dsar_requests")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });
    setRequests((data as DsarRequest[]) ?? []);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = useCallback(
    async (kind: DsarKind, notes?: string) => {
      if (!user) return { error: "not_signed_in" };
      // Prefer the SECURITY DEFINER RPC (validates auth, returns id).
      const { error: rpcErr } = await supabase.rpc("fn_request_dsar", {
        _kind: kind,
        _notes: notes ?? null,
      });
      if (rpcErr) {
        // Fallback to direct insert for older deployments.
        const { error } = await supabase.from("dsar_requests").insert({
          user_id: user.id,
          kind,
          notes: notes ?? null,
        });
        if (error) return { error: error.message };
      }
      await load();
      return { error: null };
    },
    [user, load],
  );

  const hasPending = requests.some((r) => r.status === "pending" || r.status === "in_progress");

  return { requests, loading, submit, hasPending, reload: load };
}
